import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Save, Loader2, Star, Pin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

interface Announcement {
  id: string;
  title: string;
  body: string;
  type: string;
  active: boolean;
  pinned: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
}

const emptyForm = {
  title: '',
  body: '',
  type: 'info',
  active: true,
  pinned: false,
  starts_at: '',
  ends_at: '',
};

const TYPE_OPTIONS = [
  { value: 'info',    label: 'Info',    color: 'text-electric' },
  { value: 'success', label: 'News',    color: 'text-emerald' },
  { value: 'warning', label: 'Notice',  color: 'text-gold' },
  { value: 'update',  label: 'Update',  color: 'text-fiery' },
];

export function AnnouncementsManager() {
  const { user } = useAuth();
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function fetchAll() {
    setLoading(true);
    const { data } = await supabase
      .from('announcements')
      .select('*')
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false });
    setItems(data ?? []);
    setLoading(false);
  }

  useEffect(() => { fetchAll(); }, []);

  function openCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  }

  function openEdit(a: Announcement) {
    setForm({
      title: a.title,
      body: a.body,
      type: a.type,
      active: a.active,
      pinned: a.pinned,
      starts_at: a.starts_at ? a.starts_at.slice(0, 16) : '',
      ends_at: a.ends_at ? a.ends_at.slice(0, 16) : '',
    });
    setEditingId(a.id);
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      type: form.type,
      active: form.active,
      pinned: form.pinned,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
      created_by: user?.id ?? null,
    };

    if (editingId) {
      await supabase.from('announcements').update(payload).eq('id', editingId);
      setMessage('Announcement updated.');
    } else {
      await supabase.from('announcements').insert(payload);
      setMessage('Announcement created.');
    }

    setSaving(false);
    setShowModal(false);
    fetchAll();
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this announcement?')) return;
    setDeletingId(id);
    await supabase.from('announcements').delete().eq('id', id);
    setDeletingId(null);
    fetchAll();
  }

  async function toggleActive(a: Announcement) {
    await supabase.from('announcements').update({ active: !a.active }).eq('id', a.id);
    setItems(prev => prev.map(i => i.id === a.id ? { ...i, active: !i.active } : i));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-midnight dark:text-soft-white">Announcements</h3>
          <p className="text-xs text-slate dark:text-soft-white/60 mt-0.5">
            Visible to all users on their Dashboard.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New
        </button>
      </div>

      {message && (
        <div className="mb-3 px-3 py-2 bg-emerald/10 text-emerald text-sm rounded-lg">
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-slate" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate dark:text-soft-white/50 py-6 text-center">
          No announcements yet.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div
              key={a.id}
              className="flex items-start justify-between gap-3 p-3 rounded-lg border border-sand/20 dark:border-slate/20 bg-white dark:bg-charcoal"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  {a.pinned && <Star className="h-3 w-3 text-gold fill-gold shrink-0" />}
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${TYPE_OPTIONS.find(t => t.value === a.type)?.color ?? 'text-slate'}`}>
                    {TYPE_OPTIONS.find(t => t.value === a.type)?.label ?? a.type}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${a.active ? 'bg-emerald/10 text-emerald' : 'bg-sand/30 text-slate'}`}>
                    {a.active ? 'Live' : 'Hidden'}
                  </span>
                </div>
                <p className="text-sm font-medium text-midnight dark:text-soft-white truncate">{a.title}</p>
                <p className="text-xs text-slate dark:text-soft-white/50 truncate mt-0.5">{a.body}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => toggleActive(a)}
                  className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/60 text-xs"
                  title={a.active ? 'Hide' : 'Show'}
                >
                  {a.active ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => openEdit(a)}
                  className="p-1.5 rounded hover:bg-sand/20 dark:hover:bg-slate/20 text-slate dark:text-soft-white/60"
                  title="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(a.id)}
                  disabled={deletingId === a.id}
                  className="p-1.5 rounded hover:bg-crimson/10 text-crimson/60 hover:text-crimson transition-colors"
                  title="Delete"
                >
                  {deletingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-charcoal rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate hover:text-midnight dark:hover:text-soft-white"
            >
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-base font-semibold text-midnight dark:text-soft-white mb-4">
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  placeholder="Announcement title"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Body</label>
                <textarea
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                  rows={3}
                  className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30 resize-none"
                  placeholder="Announcement message"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  >
                    {TYPE_OPTIONS.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2 pt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                      className="rounded border-sand/40 text-electric focus:ring-electric/30"
                    />
                    <span className="text-xs text-slate dark:text-soft-white/70">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.pinned}
                      onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))}
                      className="rounded border-sand/40 text-electric focus:ring-electric/30"
                    />
                    <span className="text-xs text-slate dark:text-soft-white/70">Pinned</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Starts (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.starts_at}
                    onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))}
                    className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate dark:text-soft-white/70 mb-1">Ends (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.ends_at}
                    onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))}
                    className="w-full border border-sand/40 dark:border-slate/40 rounded-lg px-3 py-2 text-sm text-midnight dark:text-soft-white bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-electric/30"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-slate dark:text-soft-white/70 hover:text-midnight dark:hover:text-soft-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.body.trim()}
                className="flex items-center gap-1.5 px-4 py-2 bg-electric text-white text-sm rounded-lg hover:bg-electric/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {editingId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
